import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from './supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getCache = new Map();
const getInflight = new Map();

// Cache token in memory for fast access
let cachedToken = null;

// Initialize: get token from Supabase session
const initToken = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    cachedToken = session?.access_token || null;
  } catch (e) {
    console.error('Init token error:', e);
  }
};
initToken();

// Listen for auth changes
supabase.auth.onAuthStateChange((event, session) => {
  cachedToken = session?.access_token || null;
  if (!session) getCache.clear();
});

const api = axios.create({
  baseURL: API,
  timeout: 8000,
});

// ---- Caché de lecturas (GET) en memoria: navegación instantánea.
// Stale-while-revalidate: un GET cacheado responde al instante SIEMPRE; si la entrada tiene más de FRESH_MS
// se vuelve a pedir en segundo plano para la próxima visita. Las escrituras invalidan solo las áreas relacionadas.
const FRESH_MS = 60 * 1000;
const NO_CACHE = /receipt|export|signed|download|folio\/generate|\/auth\//i;
const cacheKey = (config) => `${config.url}?${JSON.stringify(config.params || {})}`;
export const clearApiCache = () => getCache.clear();
export const apiCacheSize = () => getCache.size;

// Qué lecturas quedan obsoletas según el área escrita (prefijos de URL). Si no coincide ninguna, se limpia todo.
const FINANCE = ['/cobranza', '/deudores', '/income-expense', '/dashboard', '/closings', '/financial-accounts', '/fixed-expenses', '/operating-funds'];
const INVALIDATION_RULES = [
  [/^\/(cobranza|deudores|income-expense|fixed-expenses|closings|operating-funds)/, FINANCE],
  [/^\/financial-accounts/, ['/financial-accounts', '/income-expense', '/dashboard', '/operating-funds']],
  [/^\/contracts/, ['/contracts', '/units', '/properties', '/dashboard', ...FINANCE]],
  [/^\/(properties|units)/, ['/properties', '/units', '/contracts', '/dashboard', '/maintenance']],
  [/^\/tenants/, ['/tenants', '/contracts', '/cobranza', '/deudores', '/dashboard']],
  [/^\/maintenance/, ['/maintenance', '/dashboard', '/units', '/properties']],
  [/^\/employees/, ['/employees', '/financial-accounts']],
  [/^\/external-services/, ['/external-services', '/financial-accounts']],
  [/^\/finance-categories/, ['/finance-categories', '/income-expense']],
  [/^\/presence/, ['/presence']],  // heartbeat cada minuto: no debe vaciar el caché general
];
// Suscriptores de invalidación: componentes que necesitan refetch al escribir (p. ej. AccountPicker → saldos IMO)
const invalidationListeners = new Set();
export const onCacheInvalidated = (fn) => { invalidationListeners.add(fn); return () => invalidationListeners.delete(fn); };

const invalidateFor = (url = '') => {
  const path = url.replace(/^https?:\/\/[^/]+\/api/, '').split('?')[0];
  const rule = INVALIDATION_RULES.find(([re]) => re.test(path));
  const prefixes = rule ? rule[1] : null;
  if (!prefixes) getCache.clear();
  else [...getCache.keys()].forEach((k) => { if (prefixes.some((p) => k.startsWith(p))) getCache.delete(k); });
  invalidationListeners.forEach((fn) => { try { fn(prefixes); } catch { /* noop */ } });
};

const baseAdapter = axios.getAdapter(axios.defaults.adapter);
const fetchAndStore = (config, key) => {
  if (getInflight.has(key)) return getInflight.get(key);
  const p = baseAdapter(config)
    .then((res) => { getCache.set(key, { ts: Date.now(), res }); return res; })
    .finally(() => getInflight.delete(key));
  getInflight.set(key, p);
  return p;
};
api.defaults.adapter = async (config) => {
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get') {
    const res = await baseAdapter(config);
    invalidateFor(config.url);
    return res;
  }
  if (config.noCache || config.responseType === 'blob' || NO_CACHE.test(config.url || '')) return baseAdapter(config);
  const key = cacheKey(config);
  const hit = getCache.get(key);
  if (hit) {
    if (Date.now() - hit.ts > FRESH_MS) fetchAndStore(config, key).catch(() => {});
    return { ...hit.res, config };
  }
  return fetchAndStore(config, key).then((r) => ({ ...r, config }));
};

// Use cached token - synchronous, no waiting
api.interceptors.request.use(
  (config) => {
    if (cachedToken) {
      config.headers.Authorization = `Bearer ${cachedToken}`;
    }
    // Pantalla desde la que se hace la petición (la bitácora la usa como "submódulo")
    config.headers['X-UI-Path'] = `${window.location.pathname}${window.location.search}`.slice(0, 200);
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !error.config._retry) {
      // Token expired, try to refresh (only once per request)
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        cachedToken = session.access_token;
        // Retry the request
        error.config._retry = true;
        error.config.headers.Authorization = `Bearer ${cachedToken}`;
        return api.request(error.config);
      } else {
        // No valid session, redirect to login
        cachedToken = null;
        window.location.href = '/login';
      }
    }
    if (error.response?.status === 403 && error.response.headers?.['x-rbac'] === 'denied' && !error.config?.silent) {
      toast.error('No tienes permiso para realizar esta acción', { id: 'rbac-denied' });
    }
    // Step-up 403s are handled explicitly by page-level `guardIf` calls.
    // We intentionally do NOT auto-prompt on background 403s to avoid mystery
    // dialogs when a page fetches a protected resource as a side call.
    return Promise.reject(error);
  }
);

export default api;
